import { Loading } from "@components/Loading";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { useContext, useMemo } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { FirebaseContext } from "src/helpers/firebase";
import { useEventId } from "src/helpers/useEvent";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { useSimpleDocument } from "src/helpers/useSimpleDocument";
import { OracleNav } from "./OracleNav";

/** Associate landing page: their assigned oracle challenges + a global Point Exchange entry. */
export const OracleDashboard = () => {
  const { t } = useTranslation("admin");
  const eventId = useEventId();
  const navigate = useEventNavigate();
  const { auth } = useContext(FirebaseContext);
  const [user] = useAuthState(auth);
  const uid = user?.uid;
  const { getTranslatedValue } = useTranslatedKey();

  const [oracle, oracleLoading] = useSimpleDocument<OracleDoc>(`events/${eventId}/oracles/${uid}`);
  const [challenges, challengesLoading] = useSimpleCollection<Challenge>(`events/${eventId}/challenges`);

  const assignedChallenges = useMemo(() => {
    const ids = oracle?.assignedChallengeIds ?? [];
    return challenges.filter((c) => c.type === "oracle" && ids.includes(c.id));
  }, [oracle, challenges]);

  return (
    <div className="flex min-h-screen flex-col">
      <OracleNav />
      <main className="flex flex-1 flex-col items-center px-6 pb-24">
        <h1 className="mt-12 mb-2 text-center text-3xl font-semibold">{t("oracleDashboardTitle")}</h1>
        <p className="mb-8 text-center text-sm text-gray-500">{oracle?.displayName}</p>

        <Loading loading={(!!uid && oracleLoading) || challengesLoading}>
          <div className="flex w-full max-w-md flex-col gap-3">
            <h2 className="mt-2 text-lg font-semibold">{t("selectChallenge")}</h2>
            {assignedChallenges.length === 0 && (
              <div className="text-sm text-gray-500">{t("noAssignedChallenges")}</div>
            )}
            {assignedChallenges.map((challenge) => (
              <button
                key={challenge.id}
                onClick={() => navigate(`/oracle/${challenge.id}/teams`)}
                className="btn-outline btn justify-start text-left"
              >
                {getTranslatedValue(challenge, "title") || challenge.id}
              </button>
            ))}

            <div className="divider" />
            <button onClick={() => navigate(`/oracle/_exchange/teams`)} className="btn-primary btn">
              {t("pointExchange")}
            </button>
          </div>
        </Loading>
      </main>
    </div>
  );
};
